// File: webelieve-frontend/src/features/admin/components/settings/workflow-builder/WorkflowBuilder.tsx
// EF-7E: Workflow builder — trigger + action sequence editor

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  workflowApi,
  type IntakeWorkflow,
  type WorkflowAction,
  type WorkflowTriggerType,
  type WorkflowTriggerConfig,
  type WorkflowActionType,
  type CreateWorkflowData,
} from './workflowApi';

interface WorkflowBuilderProps {
  workflowId: string | null; // null = create new
  onSave: () => void;
  onCancel: () => void;
}

const ACTION_TYPES: { type: WorkflowActionType; label: string; description: string }[] = [
  { type: 'send_email', label: 'Send Email', description: 'Send email to client or team member' },
  { type: 'create_task', label: 'Create Task', description: 'Create a task for the assigned attorney' },
  { type: 'prepare_document', label: 'Prepare Document', description: 'Auto-fill and prepare a document template' },
  { type: 'set_reminder', label: 'Set Reminder', description: 'Create a follow-up reminder' },
  { type: 'assign_attorney', label: 'Assign Attorney', description: 'Auto-assign lead to an attorney' },
  { type: 'update_stage', label: 'Update Stage', description: 'Move lead to a different pipeline stage' },
];

const TRIGGER_TYPES: { type: WorkflowTriggerType; label: string; description: string }[] = [
  { type: 'stage_change', label: 'Stage Change', description: 'When a lead moves between pipeline stages' },
  { type: 'form_submission', label: 'Form Submission', description: 'When an intake form is submitted' },
  { type: 'time_elapsed', label: 'Time Elapsed', description: 'When a lead has been in a stage for X days' },
  { type: 'document_uploaded', label: 'Document Uploaded', description: 'When a client uploads a document' },
];

function generateActionId(): string {
  return `action-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
}

const WorkflowBuilder: React.FC<WorkflowBuilderProps> = ({ workflowId, onSave, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<WorkflowTriggerType>('stage_change');
  const [triggerConfig, setTriggerConfig] = useState<WorkflowTriggerConfig>({ trigger_type: 'stage_change' });
  const [actions, setActions] = useState<WorkflowAction[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [maxActions, setMaxActions] = useState(10);
  const [maxEmails, setMaxEmails] = useState(5);

  // Selected action for editing
  const [selectedActionIdx, setSelectedActionIdx] = useState<number | null>(null);

  // Load existing workflow
  useEffect(() => {
    if (!workflowId) return;
    const load = async () => {
      try {
        setLoading(true);
        const wf = await workflowApi.getWorkflow(workflowId);
        setName(wf.name);
        setDescription(wf.description || '');
        setTriggerType(wf.trigger_type);
        setTriggerConfig(wf.trigger_config);
        setActions(wf.actions);
        setIsActive(wf.is_active);
        setMaxActions(wf.max_actions_per_run);
        setMaxEmails(wf.max_emails_per_lead_per_day);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workflowId]);

  // Update trigger config when trigger type changes
  const handleTriggerTypeChange = useCallback((type: WorkflowTriggerType) => {
    setTriggerType(type);
    setTriggerConfig({ trigger_type: type });
  }, []);

  // Add action
  const handleAddAction = useCallback((type: WorkflowActionType) => {
    const label = ACTION_TYPES.find(a => a.type === type)?.label || type;
    const newAction: WorkflowAction = {
      action_id: generateActionId(),
      action_type: type,
      label,
      config: {},
      order: actions.length,
    };
    setActions(prev => [...prev, newAction]);
    setSelectedActionIdx(actions.length);
  }, [actions.length]);

  // Remove action
  const handleRemoveAction = useCallback((idx: number) => {
    setActions(prev => prev.filter((_, i) => i !== idx).map((a, i) => ({ ...a, order: i })));
    setSelectedActionIdx(null);
  }, []);

  // Move action up/down
  const handleMoveAction = useCallback((idx: number, direction: 'up' | 'down') => {
    setActions(prev => {
      const newActions = [...prev];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= newActions.length) return prev;
      [newActions[idx], newActions[swapIdx]] = [newActions[swapIdx], newActions[idx]];
      return newActions.map((a, i) => ({ ...a, order: i }));
    });
  }, []);

  // Update action config
  const handleUpdateActionConfig = useCallback((idx: number, key: string, value: any) => {
    setActions(prev => prev.map((a, i) =>
      i === idx ? { ...a, config: { ...a.config, [key]: value } } : a
    ));
  }, []);

  // Update action label
  const handleUpdateActionLabel = useCallback((idx: number, label: string) => {
    setActions(prev => prev.map((a, i) => i === idx ? { ...a, label } : a));
  }, []);

  // Save
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Workflow name is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const data: CreateWorkflowData = {
        name: name.trim(),
        description: description.trim() || undefined,
        trigger_type: triggerType,
        trigger_config: { ...triggerConfig, trigger_type: triggerType },
        actions,
        is_active: isActive,
        max_actions_per_run: maxActions,
        max_emails_per_lead_per_day: maxEmails,
      };
      if (workflowId) {
        await workflowApi.updateWorkflow(workflowId, data);
      } else {
        await workflowApi.createWorkflow(data);
      }
      onSave();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {workflowId ? 'Edit Workflow' : 'Create Workflow'}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-md hover:bg-lex-accent-hover disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Workflow'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md p-3">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Basic info */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Workflow Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., New Lead Welcome Sequence"
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What does this workflow do?"
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="rounded" />
            <span className="text-gray-700 dark:text-gray-300">Active</span>
          </label>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Max actions/run:</label>
            <input type="number" value={maxActions} onChange={e => setMaxActions(parseInt(e.target.value) || 10)} min={1} max={50} className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Max emails/lead/day:</label>
            <input type="number" value={maxEmails} onChange={e => setMaxEmails(parseInt(e.target.value) || 5)} min={1} max={20} className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white" />
          </div>
        </div>
      </div>

      {/* Trigger */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Trigger</h4>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {TRIGGER_TYPES.map(t => (
            <button
              key={t.type}
              onClick={() => handleTriggerTypeChange(t.type)}
              className={`p-3 text-left rounded-lg border transition-colors ${
                triggerType === t.type
                  ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
            >
              <p className="text-sm font-medium text-gray-900 dark:text-white">{t.label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t.description}</p>
            </button>
          ))}
        </div>

        {/* Trigger-specific config */}
        {triggerType === 'stage_change' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">From Stage (optional)</label>
              <input type="text" value={triggerConfig.from_stage || ''} onChange={e => setTriggerConfig(prev => ({ ...prev, from_stage: e.target.value || undefined }))} placeholder="Any stage" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">To Stage (optional)</label>
              <input type="text" value={triggerConfig.to_stage || ''} onChange={e => setTriggerConfig(prev => ({ ...prev, to_stage: e.target.value || undefined }))} placeholder="Any stage" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white" />
            </div>
          </div>
        )}
        {triggerType === 'time_elapsed' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Stage Name</label>
              <input type="text" value={triggerConfig.stage_name || ''} onChange={e => setTriggerConfig(prev => ({ ...prev, stage_name: e.target.value || undefined }))} placeholder="e.g., new_lead" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Days Elapsed</label>
              <input type="number" value={triggerConfig.days_elapsed || ''} onChange={e => setTriggerConfig(prev => ({ ...prev, days_elapsed: parseInt(e.target.value) || undefined }))} placeholder="7" min={1} className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white" />
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Actions ({actions.length})</h4>
        </div>

        {/* Action list */}
        <div className="space-y-2 mb-4">
          {actions.map((action, idx) => (
            <div
              key={action.action_id}
              className={`flex items-center gap-2 p-3 rounded-lg border transition-colors cursor-pointer ${
                selectedActionIdx === idx
                  ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
              }`}
              onClick={() => setSelectedActionIdx(selectedActionIdx === idx ? null : idx)}
            >
              <span className="text-xs font-mono text-gray-400 w-5">{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{action.label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {ACTION_TYPES.find(a => a.type === action.action_type)?.description}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={e => { e.stopPropagation(); handleMoveAction(idx, 'up'); }} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                </button>
                <button onClick={e => { e.stopPropagation(); handleMoveAction(idx, 'down'); }} disabled={idx === actions.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                <button onClick={e => { e.stopPropagation(); handleRemoveAction(idx); }} className="p-1 text-gray-400 hover:text-red-500">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Action config panel (appears when an action is selected) */}
        {selectedActionIdx !== null && actions[selectedActionIdx] && (
          <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg p-4 mb-4">
            <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Configure: {actions[selectedActionIdx].label}
            </h5>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Action Label</label>
                <input
                  type="text"
                  value={actions[selectedActionIdx].label}
                  onChange={e => handleUpdateActionLabel(selectedActionIdx, e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              {/* Action-type specific config */}
              <ActionConfigFields
                action={actions[selectedActionIdx]}
                onUpdate={(key, value) => handleUpdateActionConfig(selectedActionIdx, key, value)}
              />
            </div>
          </div>
        )}

        {/* Add action buttons */}
        <div className="grid grid-cols-3 gap-2">
          {ACTION_TYPES.map(at => (
            <button
              key={at.type}
              onClick={() => handleAddAction(at.type)}
              className="p-2 text-left rounded border border-dashed border-gray-300 dark:border-gray-600 hover:border-lex-accent dark:hover:border-lex-accent hover:bg-lex-state-hover dark:hover:bg-lex-state-hover transition-colors"
            >
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{at.label}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- Action-type specific config fields ---

const ActionConfigFields: React.FC<{
  action: WorkflowAction;
  onUpdate: (key: string, value: any) => void;
}> = ({ action, onUpdate }) => {
  switch (action.action_type) {
    case 'send_email':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Recipient</label>
            <select value={action.config.recipient_type || 'client'} onChange={e => onUpdate('recipient_type', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
              <option value="client">Client</option>
              <option value="assigned_attorney">Assigned Attorney</option>
              <option value="custom">Custom Email</option>
            </select>
          </div>
          {action.config.recipient_type === 'custom' && (
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Email Address</label>
              <input type="email" value={action.config.recipient_email || ''} onChange={e => onUpdate('recipient_email', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Subject</label>
            <input type="text" value={action.config.email_subject || ''} onChange={e => onUpdate('email_subject', e.target.value)} placeholder="Welcome, {{client.name}}!" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Body Template</label>
            <textarea value={action.config.email_body_template || ''} onChange={e => onUpdate('email_body_template', e.target.value)} rows={3} placeholder="Hi {{client.name}}, thank you for reaching out..." className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
        </div>
      );

    case 'create_task':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Task Title</label>
            <input type="text" value={action.config.task_title || ''} onChange={e => onUpdate('task_title', e.target.value)} placeholder="Follow up with {{client.name}}" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Description</label>
            <textarea value={action.config.task_description || ''} onChange={e => onUpdate('task_description', e.target.value)} rows={2} className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Due in (days)</label>
            <input type="number" value={action.config.task_due_days || ''} onChange={e => onUpdate('task_due_days', parseInt(e.target.value) || undefined)} min={1} placeholder="3" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
        </div>
      );

    case 'set_reminder':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Reminder Message</label>
            <input type="text" value={action.config.reminder_message || ''} onChange={e => onUpdate('reminder_message', e.target.value)} placeholder="Follow up on lead status" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Remind in (days)</label>
            <input type="number" value={action.config.reminder_days || ''} onChange={e => onUpdate('reminder_days', parseInt(e.target.value) || undefined)} min={1} placeholder="7" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
        </div>
      );

    case 'update_stage':
      return (
        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Target Stage</label>
          <select value={action.config.target_stage || ''} onChange={e => onUpdate('target_stage', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
            <option value="">Select stage...</option>
            <option value="new_lead">New Lead</option>
            <option value="contacted">Contacted</option>
            <option value="qualifying">Qualifying</option>
            <option value="converted">Converted</option>
            <option value="declined">Declined</option>
          </select>
        </div>
      );

    case 'prepare_document':
      return (
        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Document Template ID</label>
          <input type="text" value={action.config.document_template_id || ''} onChange={e => onUpdate('document_template_id', e.target.value)} placeholder="Template UUID" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
        </div>
      );

    case 'assign_attorney':
      return (
        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Attorney User ID</label>
          <input type="text" value={action.config.attorney_user_id || ''} onChange={e => onUpdate('attorney_user_id', e.target.value)} placeholder="User UUID" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
        </div>
      );

    default:
      return null;
  }
};

export default WorkflowBuilder;
